import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import type { TENANTSINTERFACE } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import { loadPayengine } from 'payengine';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { PaymentGatewaySupported } from '@fitness-force/enum';
import { EzypaySettlementComponent } from './ezypay-settlement/ezypay-settlement.component';
import { ButtonModule } from 'primeng/button';
import { PaymentGatewayMasterService } from '@fitness-force/api';

@Component({
  selector: 'settlements',
  templateUrl: './settlements.component.html',
  styleUrls: ['./settlements.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    ProgressSpinnerModule,
    ButtonModule,
    BreadcrumbComponent,
    EzypaySettlementComponent,
    AsyncPipe,
  ],
})
export class SettlementsComponent implements OnInit {
  private store = inject(Store);
  private paymentGatewayMasterService = inject(PaymentGatewayMasterService);
  private cd = inject(ChangeDetectorRef);

  showSkeleton$: BehaviorSubject<boolean> = new BehaviorSubject(
    true as boolean,
  );
  getAccessTenantList$: Observable<TENANTSINTERFACE> = new Subject();
  tenantId = 0;

  HMACSHA256: BehaviorSubject<string> = new BehaviorSubject('' as string);
  MERCHANTID: BehaviorSubject<string> = new BehaviorSubject('' as string);
  GATEWAYID: BehaviorSubject<string> = new BehaviorSubject('' as string);
  PUBLICKEY: BehaviorSubject<string> = new BehaviorSubject('' as string);

  payEngine: any;
  loading = new BehaviorSubject<boolean>(true);
  paymentGateway: any;
  downloadBtnClicked = new BehaviorSubject<boolean>(false);
  btnLoading = new BehaviorSubject<boolean>(false);

  constructor() {
    this.loading.next(true);
  }

  async ngOnInit(): Promise<void> {
    this.componentInit();
  }

  componentInit() {
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      if (this.tenantId > 0) {
        this.getPaymentGatewayList();
      }
    });
  }

  getPaymentGatewayList() {
    this.paymentGatewayMasterService
      .getPaymentGatewayMasterHMAC(this.tenantId)
      .subscribe({
        next: async (response: any) => {
          this.paymentGateway = response.paymentGateway;
          this.HMACSHA256.next(response.hmac);
          this.MERCHANTID.next(response.merchant_id);
          this.GATEWAYID.next(response.gateway_id);
          this.PUBLICKEY.next(response.public_key);
          this.paymentGateway = response.gateway_id;

          if (this.isPayEngine) {
            this.payEngine = await loadPayengine({
              publicKey: this.PUBLICKEY.value,
            });
            document.getElementById('hidPayEng')!.innerHTML =
              `<pay-engine  id="cardID"  type="payouts" view-type="settlement"
          merchant-id=${response.merchant_id} 
          hmac=${response.hmac} (success)="onSuccess($event)" (error)="onError($event)">
          </pay-engine>`;
          }
          this.loading.next(false);
          this.cd.detectChanges();
        },
        error: (error) => {
          console.log(error);
        },
      });
  }

  downloadALLData() {
    this.downloadBtnClicked.next(true);
    this.btnLoading.next(true);
    this.cd.detectChanges();
  }

  downloadCompleteEvent(event: any) {
    if (event) {
      this.downloadBtnClicked.next(false);
      this.btnLoading.next(false);
    }
  }

  get isPayEngine(): boolean {
    return (
      this.paymentGateway == PaymentGatewaySupported.PayenginUSA.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.PayenginUSA] ||
      this.paymentGateway ==
        PaymentGatewaySupported.PayenginCanada.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.PayenginCanada]
    );
  }

  get isRazorPay(): boolean {
    return (
      this.paymentGateway == PaymentGatewaySupported.RazorpayIndia.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.RazorpayIndia]
    );
  }

  get isStripe(): boolean {
    return (
      this.paymentGateway == PaymentGatewaySupported.StripeCanada.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.StripeCanada]
    );
  }

  get isTap(): boolean {
    return (
      this.paymentGateway == PaymentGatewaySupported.TapSaudi.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.TapSaudi] ||
      this.paymentGateway == PaymentGatewaySupported.TapUAE.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.TapUAE] ||
      this.paymentGateway == PaymentGatewaySupported.TapKuwait.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.TapKuwait] ||
      this.paymentGateway == PaymentGatewaySupported.TapOman.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.TapOman] ||
      this.paymentGateway == PaymentGatewaySupported.TapBahrain.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.TapBahrain] ||
      this.paymentGateway == PaymentGatewaySupported.TapEgypt.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.TapEgypt]
    );
  }

  get isEzypay(): boolean {
    return (
      this.paymentGateway ==
        PaymentGatewaySupported.EzypayAustralia.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.EzypayAustralia] ||
      this.paymentGateway ==
        PaymentGatewaySupported.EzypayMalaysia.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.EzypayMalaysia] ||
      this.paymentGateway ==
        PaymentGatewaySupported.EzypayPhilippines.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.EzypayPhilippines] ||
      this.paymentGateway ==
        PaymentGatewaySupported.EzypaySingapore.toString() ||
      this.paymentGateway ==
        PaymentGatewaySupported[PaymentGatewaySupported.EzypaySingapore]
    );
  }

  get merchant() {
    return this.MERCHANTID.getValue();
  }

  get hash() {
    return this.HMACSHA256.getValue();
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
